import cn from 'classnames';
import { currencyFormatter } from '@utils/formatting';
import { UPSELL_PLAN_BILLING_CYCLES, MONTHLY_PLAN_ID } from 'src/constants/plan';
import { getAnnualAndMonthlySplitPlanPricing } from '@utils/plans';
import styles from './planInfo.module.scss';
import { TPlansInfo } from 'types/plans.types';

const { ANNUAL, MONTHLY } = UPSELL_PLAN_BILLING_CYCLES;

type PriceProps = {
  price: number;
  planPricingSplit: boolean;
};
const Price = ({ price, planPricingSplit }: PriceProps) => {
  if (Number.isNaN(price)) return null;

  const roundedNumber = price.toFixed(2).toString();
  const [wholeNum, decimal] = roundedNumber.split('.');

  return planPricingSplit ? (
    <p className="h2 paddingTop-tiny1 paddingBottom-tiny" data-testid="plan-price">
      ${wholeNum}.{decimal}/mo
    </p>
  ) : (
    <p className="h2 paddingTop-tiny1 paddingBottom-tiny" data-testid="plan-price">
      ${wholeNum}
      <span className={cn('h4', styles.priceDecimal)}>.{decimal}</span>
    </p>
  );
};

/*
  PricingInfo
  Info about a plan's pricing
*/

type PricingInfoProps = {
  billingCycle: string;
  planInfo: TPlansInfo;
  containerClass?: string;
};
const PricingInfo = ({ billingCycle, planInfo, containerClass = '' }: PricingInfoProps) => {
  const { [MONTHLY]: monthlyPlan, [ANNUAL]: yearlyPlan } = planInfo;
  const { amount: annualPrice, name: annualName } = yearlyPlan || {};
  const { amount: monthlyPrice, name: monthlyName } = monthlyPlan || {};

  const isMonthly = billingCycle === MONTHLY;
  const isUpsellPlan = planInfo[MONTHLY]?.id === MONTHLY_PLAN_ID;
  const { oldPrice, currentPrice, planPricingSplit } = getAnnualAndMonthlySplitPlanPricing(isMonthly);
  let name = annualName;
  let price: number = annualPrice! / 12;
  let perMonth = planPricingSplit ? (
    ''
  ) : (
    <>
      <s>{currencyFormatter(2).format(monthlyPrice!)}</s> / month
    </>
  );

  const planPrice =
    planPricingSplit && isUpsellPlan ? (
      <>
        <span className="line-through  paddingRight-tiny">{oldPrice}</span>
        <span>{currentPrice} billed annually</span>
      </>
    ) : (
      `${currencyFormatter().format(annualPrice!)} billed annually`
    );
  let caption = planPrice;
  if (isMonthly) {
    name = monthlyName;
    price = monthlyPrice!;
    perMonth = planPricingSplit ? (
      <>
        <s>{oldPrice}</s>/mo
      </>
    ) : (
      '/ month'
    );
    caption = 'Annual plan, billed monthly';
  }
  // pricePerMonthStyles because secondary plan does not have a strikethrough price- to fill the void
  const pricePerMonthStyles = cn('p0 n700 paddingTop-small', {
    'marginTop-small1 paddingBottom-tiny': !isUpsellPlan && isMonthly,
    'paddingTop-small': isUpsellPlan,
  });

  return (
    <div className={cn(styles.pricingInfoContainer, containerClass)}>
      <p className="p0 n900">{name}</p>
      {planPricingSplit && <p className={pricePerMonthStyles}>{isUpsellPlan && perMonth}</p>}
      <Price price={price} planPricingSplit={planPricingSplit} />
      {!planPricingSplit && <p className="h6 paddingTop-tiny1 n900">{perMonth}</p>}
      <p className="p2 n700 paddingTop-tiny1">{caption}</p>
    </div>
  );
};

export default PricingInfo;
